import React, { useRef, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { createUseStyles } from 'react-jss';
import { ThemeContext } from 'styled-components';
import { inRange } from 'lodash';
import ScrollableNavigation from '@comparethemarketau/manor-scrollablenavigation';

import { useWindowScrollPosition } from '../../comparison/common/use_window_scroll_position';
import { PriceHeader } from '../common/price_header';
import { ProductSections, scrollMargin, Sections, stickyBarElementId } from '../common/section_config';

const useStyles = createUseStyles((theme) => ({
    stickyBarContainer: {
        top: 0,
        left: 0,
        width: '100%',
        position: 'fixed',
        zIndex: theme.zIndex[10],
        transition: 'top 0.4s'
    },
    hidden: {
        position: 'fixed',
        top: '-180px'
    }
}));

const ProductPriceStickyBar = ({ quote, pristineValue }) => {
    const theme = useContext(ThemeContext);
    const classes = useStyles({ theme });
    const scrollPosition = useWindowScrollPosition();
    const ref = useRef(null);

    const [visible, setVisible] = useState(false);
    // This state stored the closest section to the sticky bar currently.
    const [sectionTopPositions, setSectionTopPositions] = useState([]);

    // filtering Vehicle Value depending on coverType
    const filteredSections = Sections.filter((section) => section.availableOn.includes(pristineValue));

    // In the first step, we are going to get all the section top positions to check if the sticky bar is scrolling over them.
    useEffect(() => {
        setSectionTopPositions(
            Object.values(ProductSections).map((section) => {
                const { id, availableOn } = section;
                if (availableOn.includes(pristineValue)) {
                    const element = document.getElementById(id);
                    return Math.trunc(element.getBoundingClientRect().top + document.documentElement.scrollTop - ref.current.offsetHeight - scrollMargin);
                }
                return '';
            })
        );
    }, []);

    useEffect(() => {
        // sectionShiftPositions[0] is the trigger position for the first section to be visible
        if (sectionTopPositions[0] > 0 && scrollPosition >= sectionTopPositions[0]) {
            setVisible(true);
            for (let i = 0; i < sectionTopPositions.length; i += 1) {
                const sectionTop = sectionTopPositions[i];
                const nextSectionTop = i === sectionTopPositions.length - 1 ? Number.MAX_SAFE_INTEGER : sectionTopPositions[i + 1];
                if (inRange(scrollPosition, sectionTop, nextSectionTop)) {
                    break;
                }
            }
        } else {
            setVisible(false);
        }
    }, [scrollPosition]);

    return (
        <div id={stickyBarElementId} ref={ref} className={classNames(classes.stickyBarContainer, visible ? undefined : classes.hidden)}>
            <PriceHeader quote={quote} />
            <ScrollableNavigation trackingLabel="Car Product Details Page Navigation shortcuts" sections={filteredSections} scrollMargin={210} stickyBarElementId={stickyBarElementId} />
        </div>
    );
};

ProductPriceStickyBar.propTypes = {
    quote: PropTypes.object.isRequired,
    pristineValue: PropTypes.string.isRequired
};

export { ProductPriceStickyBar };
